"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Skeleton } from "@mui/material";
import { Box, Typography, Stack } from "@mui/material";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import "./index.css";
import { Loader } from "../genericComponents/MainLoader/Loader";
import { trackEvent } from "@/app/utils/branch";
import { CATEGORY_SUBTITLE } from "@/app/lib/seo/copy";

interface CategoryHeaderProps {
  categories: any;
  hubSlug: string;
}

/** Client-side hub from location (localStorage) often differs from SSR cookie — use for links. */
function useLiveHubSlug(serverSlug: string) {
  const [live, setLive] = useState(serverSlug);

  useEffect(() => {
    setLive(serverSlug);
  }, [serverSlug]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = localStorage.getItem("serviceHubSlug");
      if (!raw) return;
      const decoded = JSON.parse(atob(raw));
      if (typeof decoded === "string" && decoded.trim()) {
        setLive(decoded.trim());
      }
    } catch {
      /* keep server slug */
    }
  }, []);

  return live.replace(/^\/+|\/+$/g, "").trim() || "noida";
}

const CategoryHeader: React.FC<CategoryHeaderProps> = ({
  categories,
  hubSlug,
}) => {
  const [loading, setLoading] = useState(false);
  const liveHubSlug = useLiveHubSlug(hubSlug);

  return (
    <>
      {loading && <Loader />}
      <Stack
        spacing={{ xs: 2.25, md: 3 }}
        className="explore-categories-root"
        sx={{
          width: "100%",
          maxWidth: "100%",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Box sx={{ flex: 1, minWidth: 0, textAlign: "left" }}>
            <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.25 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background:
                    "linear-gradient(135deg, #1f7a4d 0%, #123524 100%)",
                  color: "#fff",
                  boxShadow: "0 10px 28px rgba(31, 122, 77, 0.35)",
                }}
              >
                <GridViewRoundedIcon sx={{ fontSize: 26 }} />
              </Box>
              <Box
                className="section-eyebrow explore-categories-eyebrow"
                sx={{
                  marginBottom: "0 !important",
                  borderColor: "rgba(31, 122, 77, 0.2)",
                  background: "rgba(255,255,255,0.95)",
                }}
              >
                At-home menu
              </Box>
            </Stack>
            <Typography
              variant="h4"
              component="h2"
              className="explore-categories-title"
              sx={{
                fontWeight: 800,
                fontSize: { xs: "1.65rem", sm: "2rem", md: "2.25rem" },
                lineHeight: 1.1,
                letterSpacing: "-0.035em",
                margin: 0,
                color: "#0b1710",
              }}
            >
              Choose a category
            </Typography>
            <Typography
              sx={{
                color: "#5e6d63",
                marginTop: "0.75rem",
                maxWidth: "40rem",
                lineHeight: 1.65,
                fontSize: { xs: "0.94rem", sm: "1rem" },
              }}
            >
              {CATEGORY_SUBTITLE}
            </Typography>
          </Box>
        </Stack>

        <Box
          className="explore-categories-tiles"
          sx={{
            display: { xs: "flex", md: "grid" },
            flexDirection: { xs: "row", md: undefined },
            gridTemplateColumns: {
              md: "repeat(auto-fill, minmax(168px, 1fr))",
              lg: "repeat(auto-fill, minmax(176px, 1fr))",
            },
            gap: { xs: "0.85rem", md: "1.25rem" },
            alignItems: "stretch",
            justifyContent: { xs: "flex-start", md: "stretch" },
            overflowX: { xs: "auto", md: "visible" },
            overflowY: "hidden",
            width: "100%",
            scrollSnapType: { xs: "x mandatory", md: "none" },
            WebkitOverflowScrolling: "touch",
            pb: { xs: 0.5, md: 0 },
            mx: { xs: -0.5, md: 0 },
            px: { xs: 0.5, md: 0 },
            "&::-webkit-scrollbar": { height: 6 },
            "&::-webkit-scrollbar-thumb": {
              borderRadius: 999,
              background: "rgba(31, 122, 77, 0.25)",
            },
          }}
        >
          {categories && categories.length > 0
            ? categories.map((c: any, i: number) => (
                <CategoryTile
                  key={c?.id ?? c?.slug ?? c?.name ?? `category-${i}`}
                  category={c}
                  hubSlug={liveHubSlug}
                  setLoading={setLoading}
                />
              ))
            : Array.from({ length: 7 }).map((_, i) => (
                <Skeleton
                  key={i}
                  variant="rounded"
                  sx={{
                    width: { xs: 142, md: "100%" },
                    minWidth: { xs: 142, md: 0 },
                    height: { xs: 168, md: 192 },
                    borderRadius: "20px",
                  }}
                />
              ))}
        </Box>
      </Stack>
    </>
  );
};

interface CategoryTileProps {
  category: any;
  hubSlug: string;
  setLoading: any;
}

const CategoryTile: React.FC<CategoryTileProps> = ({
  category,
  hubSlug,
  setLoading,
}) => {
  const rawSlug = (category?.slug ?? "").toString().trim();
  const href = rawSlug
    ? `/${hubSlug}/${encodeURIComponent(rawSlug)}`
    : "#";

  return (
    <Link
      prefetch={false}
      href={href}
      className="explore-category-tile-link"
      style={{
        textDecoration: "none",
        color: "inherit",
        flexShrink: 0,
        scrollSnapAlign: "start",
      }}
      onClick={(e) => {
        if (!rawSlug) e.preventDefault();
        setLoading(true);
        trackEvent("SERVICE_VIEW", category?.id);
      }}
    >
      <Box
        className="category-tile-modern explore-category-tile"
        sx={{
          display: "flex",
          flexDirection: "column",
          borderRadius: "20px",
          width: { xs: "142px", md: "100%" },
          minWidth: { xs: "142px", md: 0 },
          maxWidth: { md: "260px" },
          height: { xs: 168, md: 200 },
          mx: { md: "auto" },
          boxShadow:
            "0 12px 32px rgba(15, 23, 17, 0.08), 0 0 0 1px rgba(31, 122, 77, 0.06)",
          margin: 0,
          cursor: "pointer",
          textAlign: "center",
          alignItems: "stretch",
          position: "relative",
          overflow: "hidden",
          background:
            "linear-gradient(180deg, #ffffff 0%, #f6fbf8 100%)",
          transition:
            "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
          border: "1px solid rgba(31, 122, 77, 0.1)",
          "&:hover": {
            transform: { xs: "none", md: "translateY(-4px)" },
            boxShadow:
              "0 20px 44px rgba(31, 122, 77, 0.18), 0 0 0 1px rgba(31, 122, 77, 0.12)",
            borderColor: "rgba(31, 122, 77, 0.22)",
          },
        }}
      >
        {category?.mobileImage?.secureHttpPath ? (
          <Box sx={{ position: "relative", flex: 1, minHeight: 0, width: "100%" }}>
            <Image
              src={category.mobileImage.secureHttpPath}
              fill
              className="explore-category-img"
              style={{
                objectFit: "cover",
              }}
              alt={
                category?.name ? `${category.name} category` : "Service category"
              }
              loading="lazy"
              sizes="(max-width: 900px) 142px, 200px"
            />
          </Box>
        ) : (
          <Box
            className="explore-category-img explore-category-img--placeholder"
            sx={{
              flex: 1,
              minHeight: 100,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background:
                "linear-gradient(145deg, #e8f5ee 0%, #dff6e8 50%, #f5fbf7 100%)",
              color: "#1f7a4d",
              fontWeight: 800,
              fontSize: "1.5rem",
            }}
            aria-hidden
          >
            {(category?.name || "?").slice(0, 1).toUpperCase()}
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 0.5,
            px: 1.25,
            py: 1,
            background:
              "linear-gradient(180deg, rgba(11, 23, 17, 0.92) 0%, rgba(8, 17, 12, 0.98) 100%)",
            borderTop: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: "0.82rem", sm: "0.88rem" },
              color: "#fffef8",
              letterSpacing: "0.01em",
              lineHeight: 1.25,
              textAlign: "left",
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {category?.name ?? "Category"}
          </Typography>
          <ArrowForwardRoundedIcon
            sx={{
              fontSize: 18,
              color: "#a7e3c0",
              flexShrink: 0,
            }}
          />
        </Box>
      </Box>
    </Link>
  );
};

export default CategoryHeader;
